import type { ConversationListItemResponse } from "@redotech/effect-api-shared/models/shopper-ai/conversation/conversation";
import LayoutRightIcon from "@redotech/redo-icons/layout-right.svg";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import type { RedoHorizontalTab } from "@redotech/redo-web/arbiter-components/tabs/redo-horizontal-tabs";
import { RedoHorizontalTabs } from "@redotech/redo-web/arbiter-components/tabs/redo-horizontal-tabs";
import { Flex } from "@redotech/redo-web/flex";
import { LoadingRedoAnimation } from "@redotech/redo-web/loading-redo-animation";
import { Text } from "@redotech/redo-web/text";
import { assertNever } from "@redotech/util/type";
import { memo, type ReactNode } from "react";
import { useLocalStorage } from "usehooks-ts";
import { useCustomerOrders } from "../hooks/use-customer-orders";
import { useCustomerReturns } from "../hooks/use-customer-returns";
import { AssigneeSelector } from "./assignee-selector";
import { ChannelBadge } from "./badges/conversation-badges";
import { ConversationDetailsSection } from "./conversation-details-section";
import { ConversationSummarySection } from "./conversation-summary-section";
import { ConversationTicketFieldTitle } from "./conversation-ticket-field-title";
import { CustomerInfoSection } from "./customer-info-section";
import { OrderCard } from "./order-card";
import { PrioritySelector } from "./priority-selector";
import { ReturnCard } from "./return-card";
import { StatusSelector } from "./status-selector";
import { TagSelector } from "./tag-selector";
import { ConversationTimeline } from "./timeline/conversation-timeline";

export interface ConversationDetailsPanelProps {
  conversation: ConversationListItemResponse;
  onCollapse?: () => void;
}

/**
 * Right panel component that displays conversation details.
 * Shows overview (status, priority) and customer info placeholders.
 */
type DetailsPanelTab = "details" | "activity";

const DETAILS_PANEL_TABS: RedoHorizontalTab<DetailsPanelTab>[] = [
  { label: "Details", key: "details" },
  { label: "Activity", key: "activity" },
];

export const ConversationDetailsPanel = memo(function ConversationDetailsPanel({
  conversation,
  onCollapse,
}: ConversationDetailsPanelProps) {
  const [selectedTab, setSelectedTab] = useLocalStorage<DetailsPanelTab>(
    "redo.support-v2.conversation-details.selected-tab",
    "details",
  );

  function renderTab(tab: DetailsPanelTab): ReactNode {
    switch (tab) {
      case "details":
        return (
          <Flex
            dir="column"
            flex={1}
            gap="none"
            overflowY="auto"
            pb="md"
            px="xl"
            style={{ minHeight: 0 }}
          >
            <ConversationOverviewSection conversation={conversation} />
            <ConversationSummarySection conversation={conversation} />
            <CustomerInfoSection conversation={conversation} />
            <OrdersSection conversation={conversation} />
            <ReturnsSection conversation={conversation} />
          </Flex>
        );
      case "activity":
        return <ConversationTimeline conversationId={conversation.id} />;
      default:
        assertNever(tab);
    }
  }

  return (
    <Flex dir="column" flex={1} overflow="hidden">
      <Flex
        align="flex-end"
        borderBottomWidth="1px"
        borderColor="secondary"
        borderStyle="solid"
        shrink={0}
        style={{ height: 48 }}
      >
        <RedoHorizontalTabs
          drawBottomLine={false}
          pl="xl"
          selectedTab={selectedTab}
          setSelectedTab={setSelectedTab}
          size="sm"
          tabs={DETAILS_PANEL_TABS}
        />
        {onCollapse && (
          <Flex align="center" pr="md" style={{ height: "100%" }}>
            <RedoButton
              hierarchy="tertiary"
              IconLeading={LayoutRightIcon}
              onClick={onCollapse}
              size="sm"
            />
          </Flex>
        )}
      </Flex>
      {renderTab(selectedTab)}
    </Flex>
  );
});

interface ConversationOverviewSectionProps {
  conversation: ConversationListItemResponse;
}

const ConversationOverviewSection = memo(function ConversationOverviewSection({
  conversation,
}: ConversationOverviewSectionProps) {
  const lastResponseAt = new Date(
    Math.max(
      conversation.lastMerchantMessageAt?.getTime() ?? -1,
      conversation.lastCustomerMessageAt?.getTime() ?? -1,
    ),
  );

  return (
    <ConversationDetailsSection
      localStorageKey="redo.support-v2.conversation-details.ticket.collapsed"
      title="Ticket"
    >
      <AssigneeSelector
        conversationId={conversation.id}
        mongoAssigneeId={conversation.mongoAssigneeId}
      />
      <PrioritySelector
        conversationId={conversation.id}
        priority={conversation.priority}
      />
      <StatusSelector
        conversationId={conversation.id}
        status={conversation.status}
      />
      <Flex align="center" gap="md" justify="space-between">
        <ConversationTicketFieldTitle>Channel</ConversationTicketFieldTitle>
        {conversation.channels.map((channel) => (
          <ChannelBadge channel={channel} key={channel} size="sm" />
        ))}
      </Flex>
      <Flex align="center" gap="md" justify="space-between">
        <ConversationTicketFieldTitle>Created at</ConversationTicketFieldTitle>
        <Text fontSize="xs" textColor="primary">
          {conversation.createdAt
            ? conversation.createdAt.toLocaleString(undefined, {
                month: "short",
                day: "numeric",
                hour: "numeric",
                minute: "2-digit",
                hour12: true,
              })
            : "-"}
        </Text>
      </Flex>
      <Flex align="center" gap="md" justify="space-between">
        <ConversationTicketFieldTitle>
          Last response
        </ConversationTicketFieldTitle>
        <Text fontSize="xs" textColor="primary">
          {lastResponseAt.toLocaleString(undefined, {
            month: "short",
            day: "numeric",
            hour: "numeric",
            minute: "2-digit",
            hour12: true,
          })}
        </Text>
      </Flex>
      <TagSelector
        conversationId={conversation.id}
        currentTagIds={conversation.tagIds}
      />
    </ConversationDetailsSection>
  );
});

interface OrdersSectionProps {
  conversation: ConversationListItemResponse;
}

const OrdersSection = memo(function OrdersSection({
  conversation,
}: OrdersSectionProps) {
  const customerEmail = conversation.customer?.email;
  const { orders, pending, error } = useCustomerOrders(customerEmail);

  return (
    <ConversationDetailsSection
      localStorageKey="redo.support-v2.conversation-details.orders.collapsed"
      title="Orders"
    >
      {pending ? (
        <Flex align="center" justify="center" p="md">
          <LoadingRedoAnimation />
        </Flex>
      ) : error ? (
        <Text fontSize="xs" textColor="error">
          Failed to load orders
        </Text>
      ) : orders.length === 0 ? (
        <Text fontSize="xs" textColor="tertiary">
          No orders found
        </Text>
      ) : (
        <Flex dir="column" gap="md">
          {orders.map((order) => (
            <OrderCard key={order._id} order={order} />
          ))}
        </Flex>
      )}
    </ConversationDetailsSection>
  );
});

interface ReturnsSectionProps {
  conversation: ConversationListItemResponse;
}

const ReturnsSection = memo(function ReturnsSection({
  conversation,
}: ReturnsSectionProps) {
  const customerEmail = conversation.customer?.email;
  const { returns, pending, error } = useCustomerReturns(customerEmail);

  return (
    <ConversationDetailsSection
      localStorageKey="redo.support-v2.conversation-details.returns.collapsed"
      title="Returns"
    >
      {pending ? (
        <Flex align="center" justify="center" p="md">
          <LoadingRedoAnimation />
        </Flex>
      ) : error ? (
        <Text fontSize="xs" textColor="error">
          Failed to load returns
        </Text>
      ) : returns.length === 0 ? (
        <Text fontSize="xs" textColor="tertiary">
          No returns found
        </Text>
      ) : (
        <Flex dir="column" gap="md">
          {returns.map((returnData) => (
            <ReturnCard key={returnData._id} return={returnData} />
          ))}
        </Flex>
      )}
    </ConversationDetailsSection>
  );
});
